import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { PERMISSION_PARENTS } from '../utils/permissions';

export const PLAN_DENIED = 'Seu plano não permite acessar este conteúdo. Entre em contato com o administrador.';
const PlanContext = createContext({ ready: true, modules: null });
export const useLicensePlan = () => useContext(PlanContext);

export function LicensePlanProvider({ children, installed = process.env.REACT_APP_DISTRIBUTION === 'true' || window.location.port === '8765' }) {
  const [plan, setPlan] = useState({ ready: !installed, modules: null });
  const refreshRef = useRef(null);
  useEffect(() => {
    if (!installed) return undefined;
    let active = true;
    let inFlight = false;
    const refresh = async (force = false) => {
      if (!active || inFlight) return;
      inFlight = true;
      setPlan((current) => ({ ...current, refreshing: true, error: false }));
      try {
        const { data } = force ? await api.post('/license/recheck') : await api.get('/license/status');
        if (active) setPlan({ ready: true, modules: data.modules ?? null, online: data.online, refreshing: false });
      } catch (_) {
        if (active) setPlan((current) => ({ ...current, refreshing: false, error: true }));
      } finally {
        inFlight = false;
      }
    };
    refreshRef.current = () => refresh(true);
    refresh();
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    const timer = setInterval(() => refresh(), 30000);
    return () => { active = false; refreshRef.current = null; clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [installed]);
  return <PlanContext.Provider value={{ ...plan, refresh: () => refreshRef.current?.() }}>{children}</PlanContext.Provider>;
}

export function PlanGate({ module, children }) {
  const plan = useLicensePlan();
  if (!plan.ready) return <p role="status" className="p-6 text-sm text-slate-500">{plan.error ? 'Não foi possível consultar o plano. Tentaremos novamente em alguns instantes.' : 'Consultando os módulos do plano…'}</p>;
  const licensedModule = PERMISSION_PARENTS[module] || module;
  if (module !== 'inicio' && plan.modules !== null && !plan.modules.includes(licensedModule)) {
    return <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
      <p>{PLAN_DENIED}</p>
      <p className="mt-2 text-sm">Se o administrador já liberou este módulo, atualize a liberação abaixo.</p>
      <button type="button" disabled={plan.refreshing} onClick={plan.refresh} className="mt-4 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">{plan.refreshing ? 'Consultando liberação…' : 'Atualizar liberação'}</button>
      {plan.error && <p className="mt-3 text-sm">Não foi possível consultar a liberação. Tente novamente.</p>}
      {plan.online === false && <p className="mt-3 text-sm">Sem conexão com o servidor de licenças. Aguardando conexão para receber alterações.</p>}
    </div>;
  }
  return children;
}
